import { useEffect, useRef, useState } from 'react'
import Modal from '../UI/Modal'
import Markdown from '../common/Markdown'
import { useT } from '../../hooks/useTranslation'
import { useHaptic } from '../../hooks/useHaptic'
import { useDashboardStore } from '../../store/dashboardStore'
import { getGuideApiKeys } from '../../services/api'

const API_BASE = import.meta.env.VITE_API_URL ?? '/api'

// Гайд «Как получить ключи на бирже?». Контент читается с сервера из
// markdown-файла API_KEYS_GUIDE.md (GET /api/guide/api-keys) при каждом
// открытии — владелец правит файл напрямую, правки видны сразу.
// Открывается по кнопке-надписи в инструкции (Шаг 2): знающий юзер кнопку
// не трогает, незнающий — получает подробное объяснение одним нажатием.
// Язык берём из стора, как в InstructionModal: en → API_KEYS_GUIDE_EN.md
// (при отсутствии английского файла сервер отдаёт русский).
export default function ApiKeysGuideModal({ onClose }) {
  const t = useT()
  const haptic = useHaptic()
  const modalRef = useRef(null)
  const language = useDashboardStore(s => s.language)

  const [state, setState] = useState({ loading: true, error: null, content: '' })
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let cancelled = false
    setState(s => ({ ...s, loading: true, error: null }))
    getGuideApiKeys(language)
      .then(data => {
        if (cancelled) return
        setState({ loading: false, error: null, content: data?.content || '' })
      })
      .catch(err => {
        if (cancelled) return
        setState({ loading: false, error: err?.message || String(err), content: '' })
      })
    return () => { cancelled = true }
  }, [language, reloadKey])

  const retry = () => { haptic.light(); setReloadKey(k => k + 1) }
  const close = () => { haptic.light(); modalRef.current?.close() }

  const footer = (
    <button
      onClick={close}
      style={{
        width: '100%', padding: '14px', borderRadius: 14,
        background: 'linear-gradient(135deg, #34D399 0%, #10B981 100%)',
        color: '#fff', fontSize: 15, fontWeight: 700,
        border: 'none', cursor: 'pointer',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      {t.guideApiKeysClose}
    </button>
  )

  return (
    <Modal ref={modalRef} title={t.guideApiKeysTitle} onClose={onClose} footer={footer}>
      {state.loading ? (
        <div style={{ padding: '4px 0' }}>
          {[0, 1, 2, 3].map(i => (
            <div key={i} className="skeleton-shimmer" style={{
              height: i === 0 ? 26 : 14,
              borderRadius: 8, marginBottom: 14,
              width: i === 0 ? '60%' : i === 1 ? '100%' : i === 2 ? '85%' : '40%',
            }} />
          ))}
        </div>
      ) : state.error ? (
        <div style={{ textAlign: 'center', padding: '36px 20px' }}>
          <div style={{ fontSize: 15, color: '#fff', fontWeight: 600, marginBottom: 6 }}>{t.guideApiKeysError}</div>
          <div style={{ fontSize: 12.5, color: '#636366', marginBottom: 18 }}>{state.error}</div>
          <button
            onClick={retry}
            style={{
              padding: '12px 26px', borderRadius: 12,
              background: 'linear-gradient(135deg, #34D399 0%, #10B981 100%)',
              color: '#fff', fontSize: 14, fontWeight: 700,
              border: 'none', cursor: 'pointer',
            }}
          >
            {t.guideApiKeysRetry}
          </button>
        </div>
      ) : (
        <div style={{ padding: '2px 0 8px' }}>
          <Markdown text={state.content} baseUrl={`${API_BASE}/guide`} />
        </div>
      )}
    </Modal>
  )
}
